import React, { useCallback, useRef, useState } from "react";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";

// Replacement for window.confirm — the browser's grey system dialog that showed
// up throughout the platform. Same job, same call shape (await it, get a
// boolean), but rendered in the app's own styling.
//
// Follows the existing useToast() idiom in this folder: the hook hands back an
// element you drop into your JSX, so there's no provider to wire up.
//
//   const { confirm, ConfirmDialog } = useConfirm();
//   ...
//   if (!(await confirm({ title: "Delete this lesson?", danger: true }))) return;
//   ...
//   return (<>{ConfirmDialog}...</>);
export const useConfirm = () => {
    const [state, setState] = useState(null);
    // The promise's resolve fn, kept across renders so the buttons can settle
    // the same promise the caller is awaiting.
    const resolver = useRef(null);

    const confirm = useCallback((options = {}) => {
        setState({
            title: options.title || "Are you sure?",
            message: options.message || "",
            confirmLabel: options.confirmLabel || "Confirm",
            cancelLabel: options.cancelLabel || "Cancel",
            danger: !!options.danger,
        });
        return new Promise((resolve) => { resolver.current = resolve; });
    }, []);

    const settle = useCallback((answer) => {
        setState(null);
        // Guard against a double-settle (Escape landing on the same tick as a
        // click): a resolved promise ignores the second call, but clearing the
        // ref keeps it obvious.
        if (resolver.current) {
            resolver.current(answer);
            resolver.current = null;
        }
    }, []);

    const ConfirmDialog = state ? (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
            role="dialog"
            aria-modal="true"
            aria-label={state.title}
            onClick={() => settle(false)}
            onKeyDown={(e) => { if (e.key === "Escape") settle(false); }}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-toast"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-5 sm:p-6">
                    <div className="flex items-start gap-3">
                        <span
                            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                                state.danger ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"
                            }`}
                        >
                            <FaExclamationTriangle size={16} />
                        </span>
                        <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-gray-900">{state.title}</h3>
                            {state.message && <p className="text-sm text-gray-500 mt-1 leading-relaxed">{state.message}</p>}
                        </div>
                        <button
                            onClick={() => settle(false)}
                            aria-label="Close"
                            className="text-gray-300 hover:text-gray-500 shrink-0"
                        >
                            <FaTimes size={14} />
                        </button>
                    </div>
                </div>
                <div className="flex gap-2 px-5 sm:px-6 pb-5 sm:pb-6">
                    <button
                        onClick={() => settle(false)}
                        className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm text-gray-600 border border-gray-200 hover:bg-gray-50"
                    >
                        {state.cancelLabel}
                    </button>
                    <button
                        autoFocus
                        onClick={() => settle(true)}
                        className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm text-white ${
                            state.danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                        {state.confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    ) : null;

    return { confirm, ConfirmDialog };
};

export default useConfirm;
